import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core'
import { PlatformService } from '@services/platform.service'
import { ChildActivationEnd, Router } from '@angular/router'
import { AuthService } from '@services/auth.service'
import { AppPreloaderService } from '@services/app-preloader.service'
import { Title } from '@angular/platform-browser'
import { environment } from '@envs/environment'
import { filter } from 'rxjs/operators'
import { Subscription } from 'rxjs'

export const APP_CONTEXT_MESSAGE_TYPE = 'APP_CONTEXT'

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
	cookieConsentKey = 'silgov_cookie'
	portalUrl = environment.url_portal
	showCookieBanner = false

	private isAngularReadyNotified = false
	private readonly subscription = new Subscription()
	private readonly messageListener: (event: MessageEvent) => void

	constructor(
		private router: Router,
		private titleService: Title,
		private cdr: ChangeDetectorRef,
		private authService: AuthService,
		private platform: PlatformService,
		preloader: AppPreloaderService
	) {
		// O preloader do index.html cobre o boot inteiro e sai quando a primeira navegação assentar —
		// guards, permissões e módulo sob demanda carregados. Assinado aqui, antes da navegação inicial.
		preloader.finishAfterInitialNavigation()

		this.router.events.pipe(filter(event => event instanceof ChildActivationEnd)).subscribe(event => {
			let snapshot = (event as ChildActivationEnd).snapshot

			while (snapshot.firstChild !== null) {
				snapshot = snapshot.firstChild
			}

			const data: any = snapshot.data

			this.titleService.setTitle(data.title || 'SilGov - Sistema Integrado de Licitações')
			this.syncCookieConsent()
		})

		this.messageListener = (event: MessageEvent) => {
			this.handleMessage(event)
		}
	}

	ngOnInit() {
		this.authService.startUserExpirationScheduler()
		this.syncCookieConsent()

		window.addEventListener('message', this.messageListener)

		setTimeout(() => {
			this.notifyAngularReady()
		}, 100)

		this.subscription.add(this.platform.mobile$.subscribe(mobile => this.applyAppMode(mobile)))
	}

	ngOnDestroy(): void {
		window.removeEventListener('message', this.messageListener)
		this.subscription.unsubscribe()
	}

	acceptCookieConsent() {
		localStorage.setItem(this.cookieConsentKey, 'true')
		this.showCookieBanner = false
	}

	private syncCookieConsent() {
		if (this.platform.mobile) return
		const hasConsent = localStorage.getItem(this.cookieConsentKey) === 'true'
		this.showCookieBanner = !!this.authService.token && !hasConsent
	}

	/**
	 * Modo app (spec 058): tema escuro fixo e a classe `app-native` no `body`, que escopa os ajustes de `_app-native.scss`.
	 * Vale desde o boot (contexto salvo) e de novo quando o `APP_CONTEXT` chega na primeira abertura.
	 */
	private applyAppMode(mobile: boolean): void {
		document.body.classList.toggle('app-native', mobile)

		if (!mobile) {
			return
		}

		this.showCookieBanner = false
		document.body.setAttribute('data-bs-theme', 'dark')
		localStorage.setItem('silgov_theme', 'dark')
	}

	private handleMessage(event: MessageEvent): void {
		const data = event.data

		if (!this.isValidAppContextMessage(data)) {
			return
		}

		this.platform.add(data.platform)
		this.authService.syncNativeSession()
		this.cdr.markForCheck()
	}

	private isValidAppContextMessage(data: any): boolean {
		return data?.type === APP_CONTEXT_MESSAGE_TYPE && data?.source === 'capacitor' && typeof data?.platform === 'string'
	}

	private notifyAngularReady(): void {
		if (this.isAngularReadyNotified) {
			return
		}

		window.parent.postMessage(
			{
				type: 'ANGULAR_READY',
				source: 'angular'
			},
			'*'
		)

		this.isAngularReadyNotified = true
	}
}
